"use client";

import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Avatar as AntdAvatar } from "antd";
import { COLOR_TOKENS, FONT_TOKENS } from "~/designSystem/constants/token";

import {
  AVATAR_COLOR_MAP,
  AVATAR_OPACITY,
  AVATAR_STYLED_PROPS,
} from "../constants";
import { AvatarStatus } from "../interfaces";
import { FADE_IN, RIPPLE_OUT } from "../keyframes";

// Define ripple animation
const ripple = css`
  @keyframes rippleEffectLogo {
    0% {
      box-shadow: 0 0 0 0 var(--logo-avatar-active-outer-shadow-base);
      transform: scale(1);
    }
    50% {
      transform: scale(1);
    }
    70% {
      box-shadow: 0 0 0 10px var(--logo-avatar-active-outer-shadow-light);
      transform: scale(1);
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
      transform: scale(1);
    }
  }
`;

export interface StyledLogoAvatarProps {
  $status?: AvatarStatus;
  $stroke?: boolean;
  $size?: number;
  $hoverable?: boolean;
}

export const StyledLogoAvatar = styled(AntdAvatar, {
  shouldForwardProp: prop =>
    !AVATAR_STYLED_PROPS.LOGO.includes(prop as keyof StyledLogoAvatarProps),
})<StyledLogoAvatarProps>`
  ${ripple}

  &.ant-avatar {
    position: relative;
    border: none !important;

    ${({ $status }) => css`
      opacity: ${$status === "active"
        ? AVATAR_OPACITY.active
        : $status === "disabled"
          ? AVATAR_OPACITY.disabled
          : 1} !important;
    `}

    ${({ $hoverable, $status }) =>
      $hoverable &&
      $status !== "disabled" &&
      css`
        &:hover {
          opacity: ${AVATAR_OPACITY.hover} !important;
          transition:
            background 0.1s cubic-bezier(0.34, 1.56, 0.64, 1),
            opacity 0.1s cubic-bezier(0.34, 1.56, 0.64, 1); // Bouncy transition
        }
      `}

    animation: ${FADE_IN} 0.2s ease-in-out;

    ${({ $status }) =>
      $status === "active" &&
      css`
        --logo-avatar-active-outer-shadow-base: ${AVATAR_COLOR_MAP[
          "core-silver"
        ].activeOuterShadow};
        --logo-avatar-active-outer-shadow-medium: ${AVATAR_COLOR_MAP[
          "core-silver"
        ].activeOuterShadow50};
        --logo-avatar-active-outer-shadow-light: ${AVATAR_COLOR_MAP[
          "core-silver"
        ].activeOuterShadow100};
        animation: rippleEffectLogo 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      `}

    &::before {
      content: "";
      position: absolute;
      top: -5px;
      right: -5px;
      bottom: -5px;
      left: -5px;
      border-radius: 50%;
      z-index: -1;
      opacity: 0;

      ${({ $status }) =>
        $status === "active" &&
        css`
          background-color: transparent;
          border: 2px solid ${AVATAR_COLOR_MAP["core-silver"].activeOuterShadow};
          animation: ${RIPPLE_OUT} 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        `}
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: 50%;
      transition:
        opacity 0.25s ease,
        border 0.25s ease;
      ${({ $stroke, $status }) =>
        $stroke &&
        css`
          & {
            border: 1px solid
              ${$status === "disabled"
                ? COLOR_TOKENS.inverted[12]
                : COLOR_TOKENS.inverted[9]} !important;
          }
        `}
    }
    .ant-avatar-string {
      font-size: ${({ $size }) => `${$size ? $size * 0.5 : 10}px`} !important;
      text-transform: uppercase;
      font-weight: ${FONT_TOKENS.weight.semibold};
      color: ${COLOR_TOKENS.white.a12};
    }
  }
`;
